import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Home as HomeIcon, Landmark } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { FACILITIES, IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0B1520]/60 px-4 py-3 text-sm text-[#F7F5F0] placeholder-[#5C7285] focus:outline-none focus:border-[#E5AC3C] focus:ring-1 focus:ring-[#E5AC3C] transition-colors";

const FACILITY_INFO = [
  { icon: HomeIcon, name: "Clubhouse & pavilion hire", text: "The pavilion at West Park is available for parties, celebrations, meetings and community events." },
  { icon: Landmark, name: "Ground hire", text: "Enquire about using the ground at West Park for cricket fixtures and special occasions." },
];

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", facility: FACILITIES[1], date: "", time: "", details: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", phone: "", facility: FACILITIES[1], date: "", time: "", details: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="booking-page">
      <Seo
        title="Facility Booking — Whitley Bay Cricket Club"
        description="Book the clubhouse at West Park, hire the ground, or register interest in indoor net practice funded by Active North Tyneside."
      />
      <PageHeader
        eyebrow="West Park, Hotspur Avenue"
        title="Book our"
        accent="facilities."
        description="Clubhouse hire, ground hire and indoor nets — send a request and the club will confirm availability."
        image={IMAGES.pavilion}
        testId="booking-header"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-14 grid gap-5 md:grid-cols-3">
        {FACILITY_INFO.map((f, i) => (
          <Reveal key={f.name} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#122030] p-7" data-testid={`facility-card-${f.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <f.icon size={22} className="text-[#E5AC3C]" />
              <h2 className="mt-4 font-head text-lg font-bold">{f.name}</h2>
              <p className="mt-3 text-sm text-[#9BB0C1] leading-relaxed">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#122030] p-8 sm:p-10" data-testid="booking-form">
            <h2 className="font-head text-xl sm:text-2xl font-bold inline-flex items-center gap-3">
              <CalendarCheck size={22} className="text-[#E5AC3C]" /> Request a booking
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input required minLength={2} className={inputCls} placeholder="Your name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" />
              <input required type="email" className={inputCls} placeholder="Email address" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" />
              <input className={inputCls} placeholder="Phone (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone-input" />
              <select className={inputCls} value={form.facility}
                onChange={(e) => setForm({ ...form, facility: e.target.value })} data-testid="booking-facility-select">
                {FACILITIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <input required type="date" className={inputCls} value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date-input" />
              <input className={inputCls} placeholder="Preferred time (e.g. 18:00–21:00)" value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="booking-time-input" />
            </div>
            <textarea rows={4} className={`${inputCls} mt-4`} placeholder="Tell us about your event or session" value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="booking-details-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="booking-submit-button"
              className="mt-6 w-full sm:w-auto bg-[#E5AC3C] hover:bg-[#F3C35B] disabled:opacity-60 text-[#0B1520] font-bold px-8 py-3.5 rounded-full text-sm transition-colors"
            >
              {sending ? "Sending…" : "Send booking request"}
            </button>
          </form>
        </Reveal>
      </section>
    </main>
  );
}
